import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Play } from 'lucide-react';
import { Button } from './ui/button';

const CALENDLY_URL = "https://calendly.com/yaenlineasoft/30min";
const OWNER_IMAGE = "https://customer-assets.emergentagent.com/job_miami-soft-dev/artifacts/w1zofxz0_Kevin%20Mandina.jpg";

const HeroSection = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' },
    },
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center pt-20 overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#030305] via-[#0a0a1a] to-[#1e1b4b]/30" />
      
      {/* Grid Pattern */}
      <div className="absolute inset-0 grid-bg opacity-50" />
      
      {/* Gradient Orbs */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-violet-600/20 rounded-full blur-[128px] animate-pulse" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-[128px] animate-pulse" style={{ animationDelay: '1s' }} />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-32">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Content */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="text-left"
          >
            <motion.div variants={itemVariants} className="mb-6">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-cyan-400 text-sm font-mono tracking-wider">
                <span className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse" />
                MIAMI-BASED SOFTWARE COMPANY
              </span>
            </motion.div>

            <motion.h1
              variants={itemVariants}
              className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight leading-[1.1] mb-6"
              data-testid="hero-heading"
            >
              <span className="text-white">Custom Software</span>
              <br />
              <span className="text-white">Development Company</span>
              <br />
              <span className="gradient-text">in Miami</span>
            </motion.h1>

            <motion.p
              variants={itemVariants}
              className="text-base md:text-lg text-gray-400 leading-relaxed mb-8 max-w-xl"
              data-testid="hero-subheading"
            >
              Custom software, web apps, mobile apps, and AI-driven automations — 
              built in Miami, delivered worldwide. We transform your ideas into 
              powerful digital solutions.
            </motion.p>

            <motion.div
              variants={itemVariants}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Button
                asChild
                size="lg"
                className="bg-violet-600 hover:bg-violet-700 text-white rounded-full px-8 py-6 text-lg font-medium transition-all shadow-[0_0_20px_rgba(124,58,237,0.3)] hover:shadow-[0_0_30px_rgba(124,58,237,0.5)] btn-shine group"
                data-testid="hero-cta-primary"
              >
                <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
                  Get a Free Consultation
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </a>
              </Button>

              <Button
                variant="outline"
                size="lg"
                className="bg-transparent border border-white/20 hover:border-white/50 text-white rounded-full px-8 py-6 text-lg font-medium transition-all backdrop-blur-sm hover:bg-white/5 group"
                onClick={() => document.querySelector('#services')?.scrollIntoView({ behavior: 'smooth' })}
                data-testid="hero-cta-secondary"
              >
                <Play className="mr-2 h-5 w-5" />
                See Our Work
              </Button>
            </motion.div>

            {/* Stats */}
            <motion.div
              variants={itemVariants}
              className="flex gap-8 mt-12 pt-8 border-t border-white/10"
            >
              <div>
                <div className="text-3xl md:text-4xl font-bold stat-number">10+</div>
                <div className="text-gray-500 text-sm mt-1">Years Experience</div>
              </div>
              <div>
                <div className="text-3xl md:text-4xl font-bold stat-number">150+</div>
                <div className="text-gray-500 text-sm mt-1">Projects Delivered</div>
              </div>
              <div>
                <div className="text-3xl md:text-4xl font-bold stat-number">98%</div>
                <div className="text-gray-500 text-sm mt-1">Client Satisfaction</div>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Content - Owner Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, x: 50 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="relative hidden lg:block"
          >
            <div className="relative">
              {/* Decorative elements */}
              <div className="absolute -inset-4 bg-gradient-to-r from-violet-600/20 to-cyan-500/20 rounded-3xl blur-2xl" />
              
              {/* Main image container */}
              <div className="relative rounded-3xl overflow-hidden border border-white/10 bg-gradient-to-br from-white/5 to-transparent p-2">
                <div className="rounded-2xl overflow-hidden">
                  <img
                    src={OWNER_IMAGE}
                    alt="Kevin Mandina - Founder of Yaenlinea Soft LLC"
                    className="w-full h-auto object-cover aspect-[4/5]"
                    data-testid="hero-owner-image"
                  />
                </div>
                
                {/* Floating badge */}
                <div className="absolute bottom-6 left-6 right-6 glass rounded-xl p-4 border border-white/10">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-r from-violet-600 to-cyan-500 flex items-center justify-center">
                      <span className="text-white font-bold text-lg">KM</span>
                    </div>
                    <div>
                      <div className="text-white font-semibold">Kevin Mandina</div>
                      <div className="text-gray-400 text-sm">Founder & CEO</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Decorative floating elements */}
              <motion.div
                animate={{ y: [-10, 10, -10] }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                className="absolute -top-8 -right-8 w-24 h-24 bg-violet-600/30 rounded-full blur-xl"
              />
              <motion.div
                animate={{ y: [10, -10, 10] }}
                transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
                className="absolute -bottom-8 -left-8 w-32 h-32 bg-cyan-500/30 rounded-full blur-xl"
              />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="w-6 h-10 border-2 border-white/20 rounded-full flex justify-center pt-2"
        >
          <div className="w-1.5 h-3 bg-white/50 rounded-full" />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
